import React, { useMemo } from "react";
import styled from "styled-components";

import { coreFourSubjectList } from "@examspark/common/dist/meta/assets";
import { subjectMeta } from "@examspark/common-meta";
import { ContainerButton, Text, VSpace } from "@examspark/common-ui";
import { useDebounce } from "@examspark/common-ui/dist/hooks/useDebounce";

import SubjectIcon from "../../common/SubjectIcon";
import { FilterData } from "../Browse";
import {
  getTermAggregationBucketValue,
  useSearch,
  useSearchFn,
} from "../useSearch";

const Filter = styled(VSpace)`
  gap: 1rem;
`;

const Tabs = styled(VSpace)`
  gap: 0;
  box-shadow: 0 0 0 1px #ddd;
  border-radius: 0.5rem;
  // overflow: hidden;
`;

interface TabProps {
  selected?: boolean;
}

const Tab = styled.div<TabProps>`
  font-size: 1.3rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  color: #333;

  background-color: #fff;
  box-shadow: 0 0 0 1px #ddd;
  &:first-child {
    border-radius: 0.5rem 0.5rem 0 0;

    button {
      border-radius: 0.5rem 0.5rem 0 0;
    }
  }

  &:last-child {
    border-radius: 0 0 0.5rem 0.5rem;

    button {
      border-radius: 0 0 0.5rem 0.5rem;
    }
  }

  img {
    transition: all 0.2s ease-in-out;
    opacity: 0.5;
    filter: grayscale(70%);
  }

  ${(props) =>
    props.selected &&
    `
    background-color: #f3f3fd;
    img {
      opacity: 1;
    }
    `}

  &:hover {
    background-color: #f7f7f7;

    ${(props) => props.selected && `background-color: #f3f3fd;`}

    img {
      opacity: 1;
      filter: grayscale(0%);
    }
  }
`;

const TabSubject = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  flex: 1;
`;
const TabLabel = styled.div``;
const TabCount = styled.div`
  width: 3rem;
`;

const Count = styled.div`
  color: #fff;
  background-color: #006699;
  border-radius: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  height: 2rem;
  margin: -0.5rem 0;
`;

const Interactable = styled(ContainerButton)`
  width: 100%;
  padding: 1rem;

  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const FilterSubject = (props: {
  filterKey: keyof FilterData;
  onChange: (value: string | undefined) => void;
  filters: FilterData;
  useSearchFn?: useSearchFn;
}) => {
  const filter = useMemo(() => {
    const { subject, ...filter } = props.filters;
    return filter;
  }, [props.filters]);

  const searchFn = props.useSearchFn ?? useSearch;

  const debouncedFilter = useDebounce(filter, 500);

  const { data } = searchFn({
    queryKey: ["aggs", props.filterKey, debouncedFilter],
    searchLabel: "SearchAggsSubject",
    limit: 0,
    filter: debouncedFilter,
  });

  return (
    <Filter>
      <Text.H2>Subject Area</Text.H2>
      <Tabs>
        <Tab key="ALL" selected={props.filters[props.filterKey] == null}>
          <Interactable
            onClick={() => props.onChange(undefined)}
            title="Show all subjects"
          >
            <TabSubject>
              <SubjectIcon height="24" title="" />
              <TabLabel>All Subjects</TabLabel>
            </TabSubject>
            <TabCount>{!!data?.total && <Count>{data?.total}</Count>}</TabCount>
          </Interactable>
        </Tab>
        {coreFourSubjectList.map((subject) => {
          const count =
            data?.aggregations &&
            getTermAggregationBucketValue(
              data?.aggregations,
              "subject",
              subject,
            );

          return (
            <Tab
              key={subject}
              selected={props.filters[props.filterKey] === subject}
            >
              <Interactable
                onClick={() => props.onChange(subject)}
                title={`Filter by ${subjectMeta[subject].label} `}
              >
                <TabSubject>
                  <SubjectIcon value={subject} height="24" title="" />
                  <TabLabel>{subjectMeta[subject].label}</TabLabel>
                </TabSubject>
                <TabCount>{!!count && <Count>{count}</Count>}</TabCount>
              </Interactable>
            </Tab>
          );
        })}
      </Tabs>
    </Filter>
  );
};

export default FilterSubject;
